import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";

export const inr = (m) => (m == null ? "-" : "₹" + (m / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
export const num = (n) => (n == null ? "-" : Number(n).toLocaleString("en-IN"));
export const dt = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-");

export function SXLogo({ size = 36, word = true, light = true, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`} data-testid="sx-logo">
      <svg width={size} height={size} viewBox="0 0 48 48" aria-label="SwitchX">
        <defs><linearGradient id="sxg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#246BFD" /><stop offset="1" stopColor="#31D7FF" /></linearGradient></defs>
        <rect x="1" y="1" width="46" height="46" rx="13" fill="#0B1730" stroke="url(#sxg)" strokeWidth="1.5" />
        <path d="M13 15 L35 33 M35 15 L13 33" stroke="url(#sxg)" strokeWidth="4" strokeLinecap="round" />
        {[[13, 15], [35, 15], [13, 33], [35, 33]].map(([x, y]) => <circle key={`${x}${y}`} cx={x} cy={y} r="3.4" fill="#07111F" stroke="#31D7FF" strokeWidth="2" />)}
        <circle cx="24" cy="24" r="4.2" fill="#31D7FF" />
      </svg>
      {word && <span className={`text-xl font-bold tracking-tight ${light ? "text-white" : "text-[#07111F]"}`}>Switch<span className="text-[#31D7FF]">X</span></span>}
    </span>
  );
}

export function useLoad(url, params) {
  const [d, setD] = useState(null);
  const [at, setAt] = useState(null);
  const key = JSON.stringify(params || {});
  const load = useCallback(async () => {
    if (!url) return;
    try {
      const r = await api.get(url, { params: JSON.parse(key) });
      setD(r.data);
      setAt(new Date());
    } catch (e) { toast.error(errMsg(e)); }
  }, [url, key]);
  useEffect(() => { load(); }, [load]);
  return [d, load, at, setD];
}

const TONE = {
  success: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/30", matched: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/30", healthy: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/30",
  resolved: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/30", delivered_simulated: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/30", completed: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/30", active: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/30",
  failed: "bg-red-500/10 text-red-700 ring-red-500/30", down: "bg-red-500/10 text-red-700 ring-red-500/30", critical: "bg-red-500/10 text-red-700 ring-red-500/30", high: "bg-orange-500/10 text-orange-700 ring-orange-500/30", difference: "bg-red-500/10 text-red-700 ring-red-500/30",
  pending_verification: "bg-amber-500/10 text-amber-700 ring-amber-500/30", degraded: "bg-amber-500/10 text-amber-700 ring-amber-500/30", medium: "bg-amber-500/10 text-amber-700 ring-amber-500/30", open: "bg-amber-500/10 text-amber-700 ring-amber-500/30",
  recon_exception: "bg-fuchsia-500/10 text-fuchsia-700 ring-fuchsia-500/30", reversed: "bg-slate-500/10 text-slate-700 ring-slate-500/30", investigating: "bg-blue-500/10 text-blue-700 ring-blue-500/30", acknowledged: "bg-blue-500/10 text-blue-700 ring-blue-500/30",
};
export const Pill = ({ s, testid }) => <span data-testid={testid} className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ring-1 ${TONE[s] || "bg-slate-500/10 text-slate-600 ring-slate-400/30"}`}>{String(s ?? "-").replace(/_/g, " ")}</span>;

export const EnvTag = ({ env = "Demo" }) => <span data-testid="env-label" className="rounded-md border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-500">{env}</span>;

export const Panel = ({ title, action, children, className = "", testid }) => (
  <section data-testid={testid} className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(7,17,31,0.04)] ${className}`}>
    {(title || action) && <div className="mb-4 flex flex-wrap items-center justify-between gap-2"><h2 className="text-sm font-semibold text-[#07111F]">{title}</h2>{action}</div>}
    {children}
  </section>
);

export const Kpi = ({ label, value, sub, tone = "text-[#07111F]", testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`mt-1.5 font-mono text-2xl font-semibold tabular-nums ${tone}`}>{value}</div>
    {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
  </div>
);

export const B = ({ v = "primary", className = "", ...p }) => {
  const s = { primary: "bg-[#246BFD] text-white hover:bg-[#1f5ee0]", ghost: "text-slate-600 hover:bg-slate-100", outline: "border border-slate-300 bg-white text-slate-700 hover:border-[#246BFD] hover:text-[#246BFD]", danger: "bg-red-600 text-white hover:bg-red-700" }[v];
  return <button className={`inline-flex min-h-[38px] items-center justify-center gap-1.5 rounded-lg px-3.5 text-sm font-medium transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#246BFD] focus-visible:ring-offset-1 ${s} ${className}`} {...p} />;
};

export const inp = "min-h-[38px] rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-[#246BFD] focus:outline-none focus:ring-2 focus:ring-[#246BFD]/20";

export function Table({ head, rows, testid, onRow, empty = "No records match these filters." }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><tr>{head.map((h) => <th key={h} className="whitespace-nowrap px-3 py-2.5 font-semibold">{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={head.length} className="px-3 py-10 text-center text-slate-500">{empty}</td></tr>}
          {rows.map((r, i) => <tr key={r.key || i} onClick={onRow ? () => onRow(r.key) : undefined} data-testid={r.key ? `row-${r.key}` : undefined} className={`border-t border-slate-100 ${onRow ? "cursor-pointer hover:bg-blue-50/50" : ""}`}>{r.cells.map((c, j) => <td key={j} className="whitespace-nowrap px-3 py-2.5">{c}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

export async function download(url, params, name) {
  try {
    const r = await api.get(url, { params: { ...params, format: "csv" }, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
    toast.success("Export downloaded");
  } catch (e) { toast.error(errMsg(e)); }
}

export const Head = ({ title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-3">
    <div><h1 className="text-2xl font-semibold tracking-tight text-[#07111F]" data-testid="page-title">{title}</h1>{sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}</div>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);
